import type { ClassAttributes, ComponentType, HTMLAttributes } from 'react';
import { styled, type StyledProps } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { borderRadius } from '@atlassian/jira-common-styles/src/main.tsx';
import type { OverviewTheme } from '@atlassian/jira-custom-theme-constants/src/types.tsx';

const iconWrapperStyles = {
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	boxSizing: 'border-box',
	borderRadius: `${borderRadius}px`,
	border: `1px solid ${token('color.border')}`,
};

const smallIconWrapperStyles = {
	...iconWrapperStyles,
	width: '24px',
	height: '24px',
};

const mediumIconWrapperStyles = {
	...iconWrapperStyles,
	width: '32px',
	height: '32px',
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const WhiteSmallIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...smallIconWrapperStyles,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.icon'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.accent.gray.subtlest'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LightPurpleSmallIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...smallIconWrapperStyles,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.background.accent.purple.subtler'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.accent.purple.bolder'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LightBlueSmallIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...smallIconWrapperStyles,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.background.accent.blue.subtler'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.accent.blue.bolder'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LightTealSmallIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...smallIconWrapperStyles,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.background.accent.teal.subtler'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.accent.teal.bolder'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LightGreenSmallIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...smallIconWrapperStyles,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.background.accent.green.subtler'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.accent.green.bolder'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LightLimeSmallIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...smallIconWrapperStyles,
	color: token('color.background.accent.lime.subtler'),
	backgroundColor: token('color.background.accent.lime.bolder'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LightYellowSmallIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...smallIconWrapperStyles,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.background.accent.yellow.subtler'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.accent.yellow.bolder'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LightRedSmallIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...smallIconWrapperStyles,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.background.accent.red.subtler'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.accent.red.bolder'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LightMagentaSmallIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...smallIconWrapperStyles,
	color: token('color.background.accent.magenta.subtler'),
	backgroundColor: token('color.background.accent.magenta.bolder'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LightOrangeSmallIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...smallIconWrapperStyles,
	color: token('color.background.accent.orange.subtler'),
	backgroundColor: token('color.background.accent.orange.bolder'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DarkSmallIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...smallIconWrapperStyles,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.icon'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.accent.gray.subtlest'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PurpleSmallIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...smallIconWrapperStyles,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.background.accent.purple.bolder'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.accent.purple.subtler'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BlueSmallIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...smallIconWrapperStyles,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.background.accent.blue.bolder'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.accent.blue.subtler'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TealSmallIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...smallIconWrapperStyles,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.background.accent.teal.bolder'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.accent.teal.subtler'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GreenSmallIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...smallIconWrapperStyles,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.background.accent.green.bolder'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.accent.green.subtler'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LimeSmallIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...smallIconWrapperStyles,
	color: token('color.background.accent.lime.bolder'),
	backgroundColor: token('color.background.accent.lime.subtler'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const YellowSmallIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...smallIconWrapperStyles,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.background.accent.yellow.bolder'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.accent.yellow.subtler'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RedSmallIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...smallIconWrapperStyles,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.background.accent.red.bolder'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.accent.red.subtler'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MagentaSmallIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...smallIconWrapperStyles,
	color: token('color.background.accent.magenta.bolder'),
	backgroundColor: token('color.background.accent.magenta.subtler'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OrangeSmallIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...smallIconWrapperStyles,
	color: token('color.background.accent.orange.bolder'),
	backgroundColor: token('color.background.accent.orange.subtler'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const WhiteMediumIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...mediumIconWrapperStyles,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.icon'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.accent.gray.subtlest'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LightPurpleMediumIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...mediumIconWrapperStyles,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.background.accent.purple.subtler'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.accent.purple.bolder'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LightBlueMediumIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...mediumIconWrapperStyles,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.background.accent.blue.subtler'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.accent.blue.bolder'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LightTealMediumIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...mediumIconWrapperStyles,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.background.accent.teal.subtler'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.accent.teal.bolder'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LightGreenMediumIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...mediumIconWrapperStyles,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.background.accent.green.subtler'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.accent.green.bolder'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LightLimeMediumIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...mediumIconWrapperStyles,
	color: token('color.background.accent.lime.subtler'),
	backgroundColor: token('color.background.accent.lime.bolder'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LightYellowMediumIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...mediumIconWrapperStyles,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.background.accent.yellow.subtler'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.accent.yellow.bolder'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LightRedMediumIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...mediumIconWrapperStyles,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.background.accent.red.subtler'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.accent.red.bolder'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LightMagentaMediumIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...mediumIconWrapperStyles,
	color: token('color.background.accent.magenta.subtler'),
	backgroundColor: token('color.background.accent.magenta.bolder'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LightOrangeMediumIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...mediumIconWrapperStyles,
	color: token('color.background.accent.orange.subtler'),
	backgroundColor: token('color.background.accent.orange.bolder'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DarkMediumIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...mediumIconWrapperStyles,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.icon'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.accent.gray.subtlest'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PurpleMediumIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...mediumIconWrapperStyles,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.background.accent.purple.bolder'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.accent.purple.subtler'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BlueMediumIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...mediumIconWrapperStyles,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.background.accent.blue.bolder'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.accent.blue.subtler'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TealMediumIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...mediumIconWrapperStyles,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.background.accent.teal.bolder'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.accent.teal.subtler'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GreenMediumIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...mediumIconWrapperStyles,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.background.accent.green.bolder'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.accent.green.subtler'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LimeMediumIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...mediumIconWrapperStyles,
	color: token('color.background.accent.lime.bolder'),
	backgroundColor: token('color.background.accent.lime.subtler'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const YellowMediumIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...mediumIconWrapperStyles,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.background.accent.yellow.bolder'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.accent.yellow.subtler'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RedMediumIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...mediumIconWrapperStyles,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.background.accent.red.bolder'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.accent.red.subtler'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MagentaMediumIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...mediumIconWrapperStyles,
	color: token('color.background.accent.magenta.bolder'),
	backgroundColor: token('color.background.accent.magenta.subtler'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OrangeMediumIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...mediumIconWrapperStyles,
	color: token('color.background.accent.orange.bolder'),
	backgroundColor: token('color.background.accent.orange.subtler'),
});

type Size = 'small' | 'medium';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const WRAPPER: Record<
	Size,
	Record<
		NonNullable<OverviewTheme['themeSetting']>['value'],
		ComponentType<ClassAttributes<HTMLDivElement> & HTMLAttributes<HTMLDivElement> & StyledProps>
	>
> = {
	small: {
		white: WhiteSmallIconWrapper,
		'light-purple': LightPurpleSmallIconWrapper,
		'light-blue': LightBlueSmallIconWrapper,
		'light-teal': LightTealSmallIconWrapper,
		'light-green': LightGreenSmallIconWrapper,
		'light-yellow': LightYellowSmallIconWrapper,
		'light-red': LightRedSmallIconWrapper,
		'light-magenta': LightMagentaSmallIconWrapper,
		'light-orange': LightOrangeSmallIconWrapper,
		dark: DarkSmallIconWrapper,
		purple: PurpleSmallIconWrapper,
		blue: BlueSmallIconWrapper,
		teal: TealSmallIconWrapper,
		green: GreenSmallIconWrapper,
		yellow: YellowSmallIconWrapper,
		red: RedSmallIconWrapper,
		magenta: MagentaSmallIconWrapper,
		orange: OrangeSmallIconWrapper,
		'lightest-gray': WhiteSmallIconWrapper,
		'lightest-purple': LightPurpleSmallIconWrapper,
		'lightest-blue': LightBlueSmallIconWrapper,
		'lightest-teal': LightTealSmallIconWrapper,
		'lightest-green': LightGreenSmallIconWrapper,
		'lightest-lime': LightLimeSmallIconWrapper,
		'lightest-yellow': LightYellowSmallIconWrapper,
		'lightest-orange': LightOrangeSmallIconWrapper,
		'lightest-red': LightRedSmallIconWrapper,
		'lightest-magenta': LightMagentaSmallIconWrapper,
		'lighter-gray': WhiteSmallIconWrapper,
		'lighter-purple': LightPurpleSmallIconWrapper,
		'lighter-blue': LightBlueSmallIconWrapper,
		'lighter-teal': LightTealSmallIconWrapper,
		'lighter-green': LightGreenSmallIconWrapper,
		'lighter-lime': LightLimeSmallIconWrapper,
		'lighter-yellow': LightYellowSmallIconWrapper,
		'lighter-orange': LightOrangeSmallIconWrapper,
		'lighter-red': LightRedSmallIconWrapper,
		'lighter-magenta': LightMagentaSmallIconWrapper,
		'darker-gray': DarkSmallIconWrapper,
		'darker-purple': PurpleSmallIconWrapper,
		'darker-blue': BlueSmallIconWrapper,
		'darker-teal': TealSmallIconWrapper,
		'darker-green': GreenSmallIconWrapper,
		'darker-lime': LimeSmallIconWrapper,
		'darker-yellow': YellowSmallIconWrapper,
		'darker-orange': OrangeSmallIconWrapper,
		'darker-red': RedSmallIconWrapper,
		'darker-magenta': MagentaSmallIconWrapper,
		'darkest-gray': DarkSmallIconWrapper,
		'darkest-purple': PurpleSmallIconWrapper,
		'darkest-blue': BlueSmallIconWrapper,
		'darkest-teal': TealSmallIconWrapper,
		'darkest-green': GreenSmallIconWrapper,
		'darkest-lime': LimeSmallIconWrapper,
		'darkest-yellow': YellowSmallIconWrapper,
		'darkest-orange': OrangeSmallIconWrapper,
		'darkest-red': RedSmallIconWrapper,
		'darkest-magenta': MagentaSmallIconWrapper,
		'adaptive-gray': WhiteSmallIconWrapper,
		'adaptive-purple': PurpleSmallIconWrapper,
		'adaptive-blue': BlueSmallIconWrapper,
		'adaptive-teal': TealSmallIconWrapper,
		'adaptive-green': GreenSmallIconWrapper,
		'adaptive-magenta': MagentaSmallIconWrapper,
		'no-background': WhiteSmallIconWrapper,
	},
	medium: {
		white: WhiteMediumIconWrapper,
		'light-purple': LightPurpleMediumIconWrapper,
		'light-blue': LightBlueMediumIconWrapper,
		'light-teal': LightTealMediumIconWrapper,
		'light-green': LightGreenMediumIconWrapper,
		'light-yellow': LightYellowMediumIconWrapper,
		'light-red': LightRedMediumIconWrapper,
		'light-magenta': LightMagentaMediumIconWrapper,
		'light-orange': LightOrangeMediumIconWrapper,
		dark: DarkMediumIconWrapper,
		purple: PurpleMediumIconWrapper,
		blue: BlueMediumIconWrapper,
		teal: TealMediumIconWrapper,
		green: GreenMediumIconWrapper,
		yellow: YellowMediumIconWrapper,
		red: RedMediumIconWrapper,
		magenta: MagentaMediumIconWrapper,
		orange: OrangeMediumIconWrapper,
		'lightest-gray': WhiteMediumIconWrapper,
		'lightest-purple': LightPurpleMediumIconWrapper,
		'lightest-blue': LightBlueMediumIconWrapper,
		'lightest-teal': LightTealMediumIconWrapper,
		'lightest-green': LightGreenMediumIconWrapper,
		'lightest-lime': LightLimeMediumIconWrapper,
		'lightest-yellow': LightYellowMediumIconWrapper,
		'lightest-orange': LightOrangeMediumIconWrapper,
		'lightest-red': LightRedMediumIconWrapper,
		'lightest-magenta': LightMagentaMediumIconWrapper,
		'lighter-gray': WhiteMediumIconWrapper,
		'lighter-purple': LightPurpleMediumIconWrapper,
		'lighter-blue': LightBlueMediumIconWrapper,
		'lighter-teal': LightTealMediumIconWrapper,
		'lighter-green': LightGreenMediumIconWrapper,
		'lighter-lime': LightLimeMediumIconWrapper,
		'lighter-yellow': LightYellowMediumIconWrapper,
		'lighter-orange': LightOrangeMediumIconWrapper,
		'lighter-red': LightRedMediumIconWrapper,
		'lighter-magenta': LightMagentaMediumIconWrapper,
		'darker-gray': DarkMediumIconWrapper,
		'darker-purple': PurpleMediumIconWrapper,
		'darker-blue': BlueMediumIconWrapper,
		'darker-teal': TealMediumIconWrapper,
		'darker-green': GreenMediumIconWrapper,
		'darker-lime': LimeMediumIconWrapper,
		'darker-yellow': YellowMediumIconWrapper,
		'darker-orange': OrangeMediumIconWrapper,
		'darker-red': RedMediumIconWrapper,
		'darker-magenta': MagentaMediumIconWrapper,
		'darkest-gray': DarkMediumIconWrapper,
		'darkest-purple': PurpleMediumIconWrapper,
		'darkest-blue': BlueMediumIconWrapper,
		'darkest-teal': TealMediumIconWrapper,
		'darkest-green': GreenMediumIconWrapper,
		'darkest-lime': LimeMediumIconWrapper,
		'darkest-yellow': YellowMediumIconWrapper,
		'darkest-orange': OrangeMediumIconWrapper,
		'darkest-red': RedMediumIconWrapper,
		'darkest-magenta': MagentaMediumIconWrapper,
		'adaptive-gray': WhiteMediumIconWrapper,
		'adaptive-purple': PurpleSmallIconWrapper,
		'adaptive-blue': BlueMediumIconWrapper,
		'adaptive-teal': TealMediumIconWrapper,
		'adaptive-green': GreenMediumIconWrapper,
		'adaptive-magenta': MagentaMediumIconWrapper,
		'no-background': WhiteMediumIconWrapper,
	},
};
